import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MarkdownRenderer } from '@/components/ui/MarkdownRenderer';

export function WelcomeModal({ isOpen, onClose }) {
  const [welcomeContent, setWelcomeContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadWelcomeContent();
    }
  }, [isOpen]);

  const loadWelcomeContent = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get the welcome file path from environment variable or use default
      const welcomePath = import.meta.env.VITE_WELCOME_PATH || '/WELCOME.md';
      
      const response = await fetch(welcomePath);
      if (!response.ok) {
        throw new Error(`Failed to load welcome content: ${response.status}`);
      }
      
      const content = await response.text();
      setWelcomeContent(content);
    } catch (err) {
      console.error('Error loading welcome content:', err);
      setError(err.message);
      // Fallback content
      setWelcomeContent(`# Welcome to Sonic Groove! 🎵

Welcome to your modern web-based music player with stunning visualizations and powerful playlist management.

## Getting Started

Click the "Add Songs" button to upload your music files and start enjoying your tunes with beautiful visualizations!

*Unable to load full welcome content. Please check the console for details.*`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={handleBackdropClick}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-4xl max-h-[90vh] bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 rounded-lg shadow-2xl border border-white/10 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10 bg-black/20 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg">
                <Music className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Welcome to Sonic Groove</h2>
                <p className="text-white/60 text-sm">Your modern music experience starts here</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white/60 hover:text-white hover:bg-white/10 p-2"
              title="Close welcome (Esc)"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                <span className="ml-3 text-white/80">Loading welcome content...</span>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="text-red-400 mb-4">⚠️ Error loading welcome content</div>
                <div className="text-white/60 text-sm mb-4">{error}</div>
                <MarkdownRenderer content={welcomeContent} />
              </div>
            ) : (
              <MarkdownRenderer content={welcomeContent} />
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-white/10 bg-black/20 backdrop-blur-sm p-4 flex justify-center">
            <Button
              onClick={onClose}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8"
            >
              Get Started
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
